// Shared API client. Same contract as the original `call()` in App.js, plus:
//  - a 401 triggers one silent POST /auth/refresh and a single retry (session fix #8)
//  - if the session cannot be refreshed, a "session expired" event is broadcast so the
//    app can return to the sign-in screen instead of showing errors on every page.
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SESSION_EXPIRED_EVENT = "fyntune:session-expired";

const NO_REFRESH = new Set(["/auth/login", "/auth/refresh", "/auth/logout", "/auth/change-password"]);
let refreshing = null;

const send = (path, options) => fetch(`${API}${path}`, { credentials: "include", ...options });

const refreshSession = () => {
  if (!refreshing) {
    refreshing = send("/auth/refresh", { method: "POST" })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
};

// Keeps an active session alive (used by the inactivity timer). Resolves to false once the
// server says the session is gone.
export const keepAlive = () => refreshSession();

export const call = async (path, options = {}) => {
  let response = await send(path, options);
  if (response.status === 401 && !NO_REFRESH.has(path.split("?")[0])) {
    if (await refreshSession()) response = await send(path, options);
    if (response.status === 401) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  return data;
};

// Helpers for new pages
export const json = (method, body) => ({ method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body ?? {}) });
export const form = (method, fields = {}, files = {}) => {
  const body = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) body.append(k, typeof v === "object" && !(v instanceof Blob) ? JSON.stringify(v) : v); });
  Object.entries(files).forEach(([k, list]) => (Array.isArray(list) ? list : [list]).filter(Boolean).forEach((f) => body.append(k, f, f.name)));
  return { method, body };
};
export const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
};
